import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatInTimeZone } from 'date-fns-tz';

import { RootState } from '../../../store/store';
import {
  changeDeleteTestResultModalVisibility,
  changeShowAlertTestResultModalVisibility,
  changeUpdateBiomarkersResultsModalVisibility,
  setAlertsCount,
  setAlertsList,
  setAlertsListByAppointment,
  setSelectedTestResultId,
  setSelectedTestResultMemberName,
  setSelectedTestResultMemberGender,
  setStaffAlerts
} from '../../../store/alertsSlice';

import Modal from '../../BaseComponents/Modal';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import ConfirmModal from '../../BaseComponents/Modal/ConfirmModal';

import {
  deleteBiomarkerResult,
  getBiomarkersResultsByAppointment,
  setBiomarkerResultStatus
} from '../../../api/biomarkers';
import { readStaffNotification, getStaffAlerts } from '../../../api/staff';
import { getAppointment } from '../../../api/appointments';
import { getUnprocessedTestResultsCount } from '../../../api/testResults';
import { getNotesCount } from '../../../api/counters';
import { setNotesCount } from '../../../store/appSlice';

import {
  BiomarkerResultType,
  BiomarkerResultStatus,
  AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO
} from '../../../types/apiGeneratedTypes';

import SingleBiomarkerAlert from './SingleBiomarkerAlert';
import './ShowBiomarkersTestResultsNodal.scss';
import {
  formatDisabledBiomarkersPublishError,
  isBiomarkerActive,
} from '../../../utils/biomarkers';

// Constants
const MODAL_WIDTH = '800px';

// Types
interface ShowBiomarkersTestResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resultsType: BiomarkerResultType;
  appointmentId: number;
  member_id: string;
}

interface ErrorState {
  message: string;
  type: 'loading' | 'status' | 'general';
}

const ShowBiomarkersTestResultsModal: React.FC<ShowBiomarkersTestResultsModalProps> = ({
  isOpen,
  onClose,
  resultsType,
  appointmentId,
  member_id
}) => {
  const dispatch = useDispatch();
  
  // Local state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [isPublishingToMember, setIsPublishingToMember] = useState<boolean>(false);
  const [isSettingToAwaitingDiscussion, setIsSettingToAwaitingDiscussion] = useState<boolean>(false);
  const [isSettingToUrgent, setIsSettingToUrgent] = useState<boolean>(false);
  const [isDeletingResults, setIsDeletingResults] = useState<boolean>(false);
  const [isDeletingSingleResult, setIsDeletingSingleResult] = useState<boolean>(false);
  const [singleResultIdToDelete, setSingleResultIdToDelete] = useState<number | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [openedAccordion, setOpenedAccordion] = useState<number | null>(null);
  
  // Redux state
  const {
    alertsListByAppointment,
    alertsList,
    isDeleteTestResultModalVisible,
    selectedTestResultMemberName,
    staffAlerts
  } = useSelector((state: RootState) => state.alerts);

  // Utility functions
  const onCloseModals = useCallback(() => {
    dispatch(changeDeleteTestResultModalVisibility(false));
    dispatch(changeShowAlertTestResultModalVisibility(false));
    dispatch(setSelectedTestResultId(null));
    dispatch(setSelectedTestResultMemberName(null));
    dispatch(setAlertsListByAppointment(null));
  }, [dispatch]);

  const getBiomarkerResultIds = useCallback((
    alertsListByAppointment: AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO[]
  ): number[] => {
    return alertsListByAppointment
      ?.map((alert) => alert.result.id)
      .filter((id) => id) as number[];
  }, []);

  const updateAlertsCount = useCallback(async () => {
    try {
      const alerts = await getUnprocessedTestResultsCount();
      dispatch(setAlertsCount(alerts.count));
    } catch (error) {
      console.error('Error updating alerts count:', error);
    }
  }, [dispatch]);

  const refreshNotesCount = useCallback(async () => {
    try {
      const notes = await getNotesCount();
      dispatch(setNotesCount(notes.total_count));
    } catch (error) {
      console.error('Error refreshing notes count:', error);
    }
  }, [dispatch]);

  const refreshStaffAlerts = useCallback(async () => {
    try {
      const response = await getStaffAlerts();
      dispatch(setStaffAlerts(response.alerts));
    } catch (error) {
      console.error('Error refreshing staff alerts:', error);
    }
  }, [dispatch]);

  const extractErrorMessage = useCallback((error: any): string => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') {
      if (detail === 'Member must have nutritionist assigned.') {
        return 'Please assign a nutritionist';
      }
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((item) => item?.msg || item).join(', ');
    }
    if (error?.response?.data?.description) {
      return error.response.data.description;
    }
    if (error?.response?.data?.message) {
      const serverMessage = error.response.data.message;
      if (serverMessage === 'Member must have nutritionist assigned.') {
        return 'Please assign a nutritionist';
      }
      return serverMessage;
    }
    if (error?.response?.data?.error) {
      const serverMessage = error.response.data.error;
      if (serverMessage === 'Member must have nutritionist assigned.') {
        return 'Please assign a nutritionist';
      }
      return serverMessage;
    }
    if (error?.message && !error.message.includes('AxiosError') && !error.message.includes('Request failed')) {
      return error.message;
    }
    return 'An error occurred';
  }, []);

  const setErrorState = useCallback((message: string, type: ErrorState['type']) => {
    setError({ message, type });
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Effects
  useEffect(() => {
    if (!appointmentId) return;

    const fetchTestResults = async () => {
      try {
        setLoading(true);
        clearError();
        
        // Fetch appointment data to get member information
        if (resultsType === BiomarkerResultType.UNPROCESSED) {
          // Only fetch UNPROCESSED results for alerts 
          const unprocessedResponse = await getBiomarkersResultsByAppointment(
            appointmentId,
            BiomarkerResultType.UNPROCESSED
          ).catch(() => ({ biomarkers: [] }));

          dispatch(setAlertsListByAppointment(unprocessedResponse.biomarkers || []));
        } else {
          const appointmentData = await getAppointment(appointmentId);
          if (appointmentData?.member?.gender) {
            dispatch(setSelectedTestResultMemberGender(appointmentData.member.gender));
          }
          const [unprocessedResponse, publishedResponse] = await Promise.all([
            getBiomarkersResultsByAppointment(appointmentId, BiomarkerResultType.UNPROCESSED).catch(() => ({ biomarkers: [] })),
            getBiomarkersResultsByAppointment(appointmentId, BiomarkerResultType.PUBLISHED).catch(() => ({ biomarkers: [] }))
          ]);

          const allBiomarkers = [
            ...(unprocessedResponse.biomarkers || []),
            ...(publishedResponse.biomarkers || [])
          ];

          const uniqueBiomarkers = allBiomarkers.filter((biomarker, index, self) =>
            index === self.findIndex(b => b.result.id === biomarker.result.id)
          );

          dispatch(setAlertsListByAppointment(uniqueBiomarkers));
        }
      } catch (error) {
        console.error('Error fetching test results:', error);
        const errorMessage = extractErrorMessage(error);
        setErrorState(errorMessage, 'loading');
      } finally {
        setLoading(false);
      }
    };

    fetchTestResults();
  }, [appointmentId, dispatch, clearError, setErrorState, extractErrorMessage, resultsType]);

  const getDisabledBiomarkerNames = useCallback(
    (
      alerts: AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO[]
    ): string[] => {
      return alerts
        .filter((alert) => !isBiomarkerActive(alert.biomarker))
        .map((alert) => alert.biomarker.name);
    },
    []
  );

  // Action handlers
  const onPublishToMember = useCallback(async () => {
    if (!alertsListByAppointment) return;

    setIsPublishingToMember(true);
    clearError();

    const disabledBiomarkerNames = getDisabledBiomarkerNames(alertsListByAppointment);
    if (disabledBiomarkerNames.length > 0) {
      setErrorState(
        formatDisabledBiomarkersPublishError(disabledBiomarkerNames),
        'status'
      );
      setIsPublishingToMember(false);
      return;
    }

    try {
      const results_ids = getBiomarkerResultIds(alertsListByAppointment);
      await setBiomarkerResultStatus({
        results_ids,
        status: BiomarkerResultStatus.APPROVED,
        appointment_id: appointmentId
      });
      
      if (staffAlerts) {
        const correspondingStaffAlert = staffAlerts.find(alert => 
          alert.appointment_id === appointmentId || 
          alert.metadata?.appointment_id === appointmentId
        );
        
        if (correspondingStaffAlert && !correspondingStaffAlert.is_read) {
          try {
            await readStaffNotification(correspondingStaffAlert.id);
            await refreshStaffAlerts();
          } catch (error) {
            console.error('Error marking staff alert as read:', error);
          }
        }
      }
      
      if (alertsList) {
        const filteredAlerts = alertsList.filter(
          (alert) => alert.appointment_id !== appointmentId
        );
        dispatch(setAlertsList(filteredAlerts));
      }
      
      await Promise.all([
        updateAlertsCount(),
        refreshNotesCount(),
        refreshStaffAlerts()
      ]);
      
      dispatch(changeShowAlertTestResultModalVisibility(false));
      onCloseModals();
    } catch (error) {
      console.error('Error publishing to member:', error);
      const errorMessage = extractErrorMessage(error);
      setErrorState(errorMessage, 'status');
    } finally {
      setIsPublishingToMember(false);
    }
  }, [
    alertsListByAppointment,
    appointmentId,
    staffAlerts,
    alertsList,
    getBiomarkerResultIds,
    getDisabledBiomarkerNames,
    refreshStaffAlerts,
    updateAlertsCount,
    refreshNotesCount,
    dispatch,
    onCloseModals,
    clearError,
    setErrorState,
    extractErrorMessage
  ]);

  const onSetToAwaitingDiscussion = useCallback(async () => {
    if (!alertsListByAppointment) return;

    setIsSettingToAwaitingDiscussion(true);
    clearError();

    try {
      const results_ids = getBiomarkerResultIds(alertsListByAppointment);
      await setBiomarkerResultStatus({
        results_ids,
        status: BiomarkerResultStatus.AWAITING,
        appointment_id: appointmentId
      });
      await Promise.all([updateAlertsCount(), refreshNotesCount(), refreshStaffAlerts()]);
      onCloseModals();
    } catch (error) {
      console.error('Error setting status to Awaiting Discussion:', error);
      const errorMessage = extractErrorMessage(error);
      setErrorState(errorMessage, 'status');
    } finally {
      setIsSettingToAwaitingDiscussion(false);
    }
  }, [
    alertsListByAppointment,
    appointmentId,
    getBiomarkerResultIds,
    updateAlertsCount,
    refreshNotesCount,
    refreshStaffAlerts,
    onCloseModals,
    clearError,
    setErrorState,
    extractErrorMessage
  ]);

  const onSetToUrgentResult = useCallback(async () => {
    if (!alertsListByAppointment) return;

    setIsSettingToUrgent(true);
    clearError();

    try {
      const results_ids = getBiomarkerResultIds(alertsListByAppointment);
      await setBiomarkerResultStatus({
        results_ids,
        status: BiomarkerResultStatus.URGENT_RESULT,
        appointment_id: appointmentId
      });
      await Promise.all([updateAlertsCount(), refreshNotesCount(), refreshStaffAlerts()]);
      onCloseModals();
    } catch (error) {
      console.error('Error setting status to Urgent Result:', error);
      const errorMessage = extractErrorMessage(error);
      setErrorState(errorMessage, 'status');
    } finally {
      setIsSettingToUrgent(false);
    }
  }, [
    alertsListByAppointment,
    appointmentId,
    getBiomarkerResultIds,
    updateAlertsCount,
    refreshNotesCount,
    refreshStaffAlerts,
    onCloseModals,
    clearError,
    setErrorState,
    extractErrorMessage
  ]);

  const removeAppointmentFromAlerts = useCallback(() => {
    if (alertsList) {
      const filteredAlerts = alertsList.filter(
        (alert) => alert.appointment_id !== appointmentId
      );
      dispatch(setAlertsList(filteredAlerts));
    }

    if (staffAlerts) {
      const updatedStaffAlerts = staffAlerts.filter((alert) => {
        const alertAppointmentId = alert.metadata?.appointment_id || alert.appointment_id;
        const isBiomarkerAlert = alert.type === 'biomarker_pending' || alert.type === 'biomarker_awaiting';
        const shouldRemove = isBiomarkerAlert && alertAppointmentId === appointmentId;
        return !shouldRemove;
      });
      dispatch(setStaffAlerts(updatedStaffAlerts));
    }
  }, [alertsList, staffAlerts, appointmentId, dispatch]);

  const markStaffAlertAsReadIfNeeded = useCallback(async () => {
    if (!staffAlerts) return;

    const alertToMarkAsRead = staffAlerts.find(alert => {
      const alertAppointmentId = alert.metadata?.appointment_id || alert.appointment_id;
      const isBiomarkerAlert = alert.type === 'biomarker_pending' || alert.type === 'biomarker_awaiting';
      return isBiomarkerAlert && alertAppointmentId === appointmentId && !alert.is_read;
    });

    if (alertToMarkAsRead) {
      try {
        await readStaffNotification(alertToMarkAsRead.id);
      } catch (error) {
        // Silently continue if marking as read fails
      }
    }
  }, [staffAlerts, appointmentId]);

  const handleDeleteResults = useCallback(async () => {
    if (!alertsListByAppointment) return;

    setIsDeletingResults(true);
    clearError();

    try {
      await markStaffAlertAsReadIfNeeded();

      const results_ids = getBiomarkerResultIds(alertsListByAppointment);

      if (results_ids.length === 0) {
        setErrorState('No biomarker results to delete', 'status');
        return;
      }

      await deleteBiomarkerResult(results_ids);

      dispatch(changeDeleteTestResultModalVisibility(false));
      removeAppointmentFromAlerts();

      await Promise.all([updateAlertsCount(), refreshNotesCount(), refreshStaffAlerts()]);
      onCloseModals();
    } catch (error) {
      console.error('Error deleting results:', error);
      const errorMessage = extractErrorMessage(error);
      setErrorState(errorMessage, 'status');
    } finally {
      setIsDeletingResults(false);
    }
  }, [
    alertsListByAppointment,
    getBiomarkerResultIds,
    markStaffAlertAsReadIfNeeded,
    removeAppointmentFromAlerts,
    dispatch,
    updateAlertsCount,
    refreshNotesCount,
    refreshStaffAlerts,
    onCloseModals,
    clearError,
    setErrorState,
    extractErrorMessage
  ]);

  const handleDeleteSingleResult = useCallback(async () => {
    if (!singleResultIdToDelete || !alertsListByAppointment) return;

    setIsDeletingSingleResult(true);
    clearError();

    try {
      const resultIdToDelete = singleResultIdToDelete;
      await deleteBiomarkerResult([resultIdToDelete]);

      const isCurrentAppointmentResult = alertsListByAppointment.some(
        (alert) => alert.result.id === resultIdToDelete
      );

      setSingleResultIdToDelete(null);
      setHistoryRefreshKey((prev) => prev + 1);

      if (!isCurrentAppointmentResult) {
        return;
      }

      const updatedList = alertsListByAppointment.filter(
        (alert) => alert.result.id !== resultIdToDelete
      );

      dispatch(setAlertsListByAppointment(updatedList));

      if (
        openedAccordion &&
        alertsListByAppointment.some(
          (alert) =>
            alert.biomarker.id === openedAccordion &&
            alert.result.id === resultIdToDelete
        )
      ) {
        setOpenedAccordion(null);
      }

      if (updatedList.length === 0) {
        await markStaffAlertAsReadIfNeeded();
        removeAppointmentFromAlerts();
        await Promise.all([updateAlertsCount(), refreshNotesCount(), refreshStaffAlerts()]);
        onCloseModals();
      } else {
        await updateAlertsCount();
      }
    } catch (error) {
      console.error('Error deleting biomarker result:', error);
      const errorMessage = extractErrorMessage(error);
      setErrorState(errorMessage, 'status');
    } finally {
      setIsDeletingSingleResult(false);
    }
  }, [
    singleResultIdToDelete,
    alertsListByAppointment,
    openedAccordion,
    dispatch,
    markStaffAlertAsReadIfNeeded,
    removeAppointmentFromAlerts,
    updateAlertsCount,
    refreshNotesCount,
    refreshStaffAlerts,
    onCloseModals,
    clearError,
    setErrorState,
    extractErrorMessage
  ]);

  const onCloseDeleteModal = useCallback(() => {
    dispatch(changeDeleteTestResultModalVisibility(false));
  }, [dispatch]);

  const onChangeBiomarkersResultsClick = useCallback(() => {
    dispatch(changeUpdateBiomarkersResultsModalVisibility(true));
    dispatch(changeShowAlertTestResultModalVisibility(false));
  }, [dispatch]);

  // Computed values
  const hasUnprocessedResults = alertsListByAppointment?.some(
    (alert) => alert.result.status === BiomarkerResultStatus.PENDING ||
               alert.result.status === BiomarkerResultStatus.URGENT_RESULT ||
               alert.result.status === BiomarkerResultStatus.AWAITING
  );
  const isPendingStatus = alertsListByAppointment?.some(
    (alert) => alert.result.status === BiomarkerResultStatus.PENDING
  );
  const hasResults = alertsListByAppointment && alertsListByAppointment.length > 0;
  const modalTitle = alertsListByAppointment?.[0]?.result?.appointment_date
    ? formatInTimeZone(
        new Date(alertsListByAppointment[0].result.appointment_date),
        'UTC',
        'dd/MM/yyyy'
      )
    : undefined;

  // Render functions
  const renderBiomarkerAlerts = () => {
    if (loading) return <Loader />;
    
    if (error?.type === 'loading') {
      return (
        <div className="error-container">
          <p>Error loading results data: {error.message}</p>
        </div>
      );
    }
    
    if (!hasResults) {
      return <div className="no-results">No results found</div>;
    }
    
    return alertsListByAppointment.map((alert) => (
      <SingleBiomarkerAlert
        key={alert.result.id}
        alertData={alert}
        openedAccordion={openedAccordion}
        setOpenedBiomarker={setOpenedAccordion}
        member_id={member_id}
        showDeleteButton={resultsType === BiomarkerResultType.UNPROCESSED}
        onDeleteResult={setSingleResultIdToDelete}
        historyRefreshKey={historyRefreshKey}
      />
    ));
  };

  const renderFooter = () => {
    if (!hasUnprocessedResults) return null;

    return (
      <div className='show-biomarkers-test-results-modal__footer'>
        <div className='edit-and-delete-btns-wrapper'>
          <Button
            backgroundColor='#EBEFF3'
            color='#000000'
            text='Edit'
            onClick={onChangeBiomarkersResultsClick}
          />
          <Button
            backgroundColor='#EBEFF3'
            color='#DC3545'
            text='Delete all biomarker results'
            onClick={() => dispatch(changeDeleteTestResultModalVisibility(true))}
          />
        </div>
        <div className='divider' style={{ margin: '0' }} />
        {isPendingStatus && (
          <Button
            backgroundColor='#EBEFF3'
            color='#000000'
            text='Set to Awaiting Discussion'
            isLoading={isSettingToAwaitingDiscussion}
            onClick={onSetToAwaitingDiscussion}
          />
        )}
        <Button
          backgroundColor='#EBEFF3'
          color='#000000'
          text='Urgent Result'
          isLoading={isSettingToUrgent}
          onClick={onSetToUrgentResult}
        />
        <Button
          text='Publish to Member'
          onClick={onPublishToMember}
          isLoading={isPublishingToMember}
        />
        {error && error.type === 'status' && (
          <div className='error-message'>{error.message}</div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      width={MODAL_WIDTH}
      memberName={selectedTestResultMemberName ?? ''}
    >
      <div className='alerts-modal-content-wrapper'>
        <div className='show-biomarkers-test-results-modal__scroll-area'>
          <div className='show-biomarkers-test-results-modal'>
            {renderBiomarkerAlerts()}
          </div>
        </div>
        {renderFooter()}
      </div>
      
      {isDeleteTestResultModalVisible && resultsType === BiomarkerResultType.UNPROCESSED && (
        <ConfirmModal
          isOpen={isDeleteTestResultModalVisible}
          onClose={onCloseDeleteModal}
          onConfirmClick={handleDeleteResults}
          title='Delete all biomarker results?'
          subtitle={`This action will permanently remove all ${alertsListByAppointment?.length ?? 0} biomarker results for this appointment. Once deleted, they cannot be restored.`}
          loading={isDeletingResults}
        />
      )}
      {singleResultIdToDelete !== null && resultsType === BiomarkerResultType.UNPROCESSED && (
        <ConfirmModal
          isOpen={singleResultIdToDelete !== null}
          onClose={() => {
            if (!isDeletingSingleResult) {
              setSingleResultIdToDelete(null);
            }
          }}
          onConfirmClick={handleDeleteSingleResult}
          title='Delete biomarker result?'
          subtitle='This action will permanently remove this result. Once deleted, it cannot be restored.'
          loading={isDeletingSingleResult}
        />
      )}
    </Modal>
  );
};

export default ShowBiomarkersTestResultsModal;
